import { faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { cn } from '@/lib/utils/cn';
import type { SectionNode } from './types';

export function TreeNode({
  node,
  depth,
}: {
  node: SectionNode;
  depth: number;
}) {
  const hasChildren = !!node.children?.length;
  const [open, setOpen] = useState(true);

  if (!hasChildren) {
    return (
      <button
        type='button'
        onClick={node.onClick}
        disabled={!node.onClick}
        // Stable hooks for tests — asserting the highlight's Tailwind classes
        // would break on any restyle.
        data-active={node.isActive ? 'true' : undefined}
        data-matched={node.isMatched ? 'true' : undefined}
        className={cn(
          'flex items-center gap-1.5 py-1.5 px-2 rounded text-left transition-colors w-full',
          // A search hit *is* the selection while results are showing: callers
          // suppress `isActive` and mark the matched leaves instead, so exactly
          // one set of rows ever carries the highlight. Sharing the styling
          // keeps the two states visually identical without merging the flags,
          // which stay semantically separate (parity with the old FE,
          // DELTA-2461).
          node.isActive || node.isMatched
            ? 'bg-primary/10 text-primary font-medium hover:bg-primary/15'
            : 'hover:bg-muted/60 text-foreground',
          !node.onClick && 'cursor-default',
        )}
        style={{ paddingLeft: `${(depth + 1) * 10}px` }}
      >
        <span className='text-xs flex-1 truncate'>{node.label}</span>
        {node.filled !== undefined && node.total !== undefined && (
          <span
            className={cn(
              'text-xs tabular-nums shrink-0',
              node.filled === node.total && node.total > 0
                ? 'text-success'
                : 'text-muted-foreground',
            )}
          >
            {node.filled}/{node.total}
          </span>
        )}
      </button>
    );
  }

  return (
    <div className='flex flex-col'>
      <button
        type='button'
        onClick={() => setOpen(o => !o)}
        className='flex items-center gap-1.5 py-1.5 px-1 rounded hover:bg-muted/60 transition-colors text-left w-full'
        style={{ paddingLeft: `${depth * 10}px` }}
      >
        <FontAwesomeIcon
          icon={faChevronRight}
          className={cn(
            'w-3 h-3 text-muted-foreground transition-transform shrink-0',
            open && 'rotate-90',
          )}
        />
        <span className='text-xs font-medium text-foreground flex-1 truncate'>
          {node.label}
        </span>
        {node.filled !== undefined && node.total !== undefined && (
          <span className='text-xs text-muted-foreground tabular-nums shrink-0'>
            {node.filled}/{node.total}
          </span>
        )}
      </button>
      {open && (
        <div className='flex flex-col border-l border-border ml-3 pl-0.5'>
          {node.children!.map(child => (
            <TreeNode key={child.id} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}
